import { ApplianceType } from '@alpinehomeair/common';
import clsx from 'clsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import SpaceInfoCard from 'components/structure-page/2d/space-info-card';
import { BTUsType } from 'data/types';

type SpaceInfoPopoverProps = Readonly<{
  label: string;
  displayLabel?: string;
  zoom?: number;
  widthFt: number;
  heightFt: number;
  BTUs?: BTUsType;
  showBTUs?: boolean;
  hasSectionColor?: boolean;
  sectionTextColor?: string;
  applianceCount?: number;
  showApplianceCount?: boolean;
  appliances?: ApplianceType[];
  onRemoveAppliance?: (applianceUniqueId: string) => void;
  isSmallSpace?: boolean;
  squareFootage?: number | null;
  showTriggerDimensions?: boolean;
  showHoverDimensions?: boolean;
  dimensionsText?: string;
}>;

export default function SpaceInfoPopover({
  label,
  displayLabel,
  zoom = 1,
  widthFt,
  heightFt,
  BTUs,
  showBTUs = false,
  hasSectionColor = false,
  sectionTextColor,
  applianceCount = 0,
  showApplianceCount = false,
  appliances = [],
  onRemoveAppliance,
  isSmallSpace = false,
  squareFootage,
  showTriggerDimensions = false,
  showHoverDimensions = true,
  dimensionsText
}: SpaceInfoPopoverProps) {
  const triggerLabel = displayLabel ?? label;
  const inverseScale = 1 / (zoom > 0 ? zoom : 1);
  const isInteractive = showApplianceCount && !!onRemoveAppliance;
  const triggerColor =
    hasSectionColor && sectionTextColor
      ? { color: sectionTextColor }
      : undefined;

  return (
    <Popover hoverEnabled closeOnHoverLeave={!isInteractive} placement='top'>
      <PopoverTrigger asChild>
        <div className='absolute inset-0 flex cursor-pointer flex-col items-center justify-center overflow-hidden px-1'>
          <div
            className='flex max-w-full flex-col items-center'
            style={{
              transform: `scale(${inverseScale})`,
              transformOrigin: 'center'
            }}
          >
            <div
              className='whitespace-nowrap text-center text-sm text-black'
              style={triggerColor}
            >
              {triggerLabel}
            </div>
            {showTriggerDimensions && dimensionsText && (
              <div
                className={
                  hasSectionColor
                    ? 'whitespace-nowrap text-xs'
                    : 'whitespace-nowrap text-xs text-gray-500'
                }
                style={
                  hasSectionColor && sectionTextColor
                    ? { color: sectionTextColor, opacity: 0.8 }
                    : undefined
                }
              >
                {dimensionsText}
              </div>
            )}
            {showApplianceCount && !isSmallSpace && (
              <div
                className='text-xs text-gray-500'
                style={
                  hasSectionColor && sectionTextColor
                    ? { color: sectionTextColor, opacity: 0.8 }
                    : undefined
                }
              >
                {applianceCount}{' '}
                {applianceCount === 1 ? 'appliance' : 'appliances'}
              </div>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={clsx(
          'z-50 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5',
          !isInteractive && 'pointer-events-none'
        )}
      >
        <SpaceInfoCard
          label={label}
          widthFt={widthFt}
          heightFt={heightFt}
          BTUs={BTUs}
          showBTUs={showBTUs}
          hasSectionColor={hasSectionColor}
          sectionTextColor={sectionTextColor}
          applianceCount={applianceCount}
          showApplianceCount={showApplianceCount}
          appliances={appliances}
          onRemoveAppliance={onRemoveAppliance}
          squareFootage={squareFootage}
          showDimensions={showHoverDimensions}
        />
      </PopoverContent>
    </Popover>
  );
}
